import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  checkUserConsent,
  getTermsContent,
  setUserConsent,
} from '../../auth/_requests';
import { Checkbox } from '../../helpers/UI/Checkbox/Checkbox';
import { showToast } from '../../helpers/utils/showToast';
import { AuthBrandedLoader } from '../Login/AuthBrandedLoader';
import { AuthPageShell } from '../Login/AuthPageShell';
import loginStyles from '../Login/Login.module.css';
import styles from './AcceptTerms.module.css';

export function AcceptTerms() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsId, setTermsId] = useState('');
  const [notFound, setNotFound] = useState('');
  const [checked, setChecked] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth?.data.token) return;

      setLoading(true);
      try {
        const consentResponse = await checkUserConsent(auth.data.token);
        if (!consentResponse?.data?.isUserConsentRequired) {
          navigate('/dashboard', { replace: true });
          return;
        }

        const termsResponse = await getTermsContent();
        if (termsResponse?.data) {
          setTermsContent(termsResponse.data.value);
          setTermsId(String(termsResponse.data.termsAndConditionId));
        } else {
          setNotFound('Unable to find the terms and conditions');
        }
      } catch (error) {
        console.error('Error fetching terms data:', error);
        setNotFound('Unable to find the terms and conditions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth, navigate]);

  const handlePrimaryAction = async () => {
    if (loading || notFound) {
      navigate('/dashboard');
      return;
    }

    if (!hasAcknowledged) {
      setHasAcknowledged(true);
      return;
    }

    if (!checked) {
      showToast({
        message: 'Please read and accept the terms and conditions to proceed.',
        type: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      await setUserConsent({ termsAndConditionId: termsId }, auth?.data.token || '');
      localStorage.removeItem('isSignUp');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error setting user consent:', error);
      showToast({
        message: 'Unable to save your consent. Please try again.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }

  const isAcceptDisabled =
    submitting || (hasAcknowledged && !checked) || (!hasAcknowledged && Boolean(notFound));

  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <main className={`${loginStyles.main} ${loginStyles.mainWide} ${styles.termsFlow}`}>
          <div className={styles.headerOuter}>
            <p className={styles.pageHeader}>Accept terms</p>
          </div>

          <p className={styles.introText}>
            Please review and accept our Terms of Service to activate your account.
          </p>

          {!notFound ? (
            <>
              <div className={styles.termsPanel}>
                <div
                  className={styles.termsContent}
                  dangerouslySetInnerHTML={{ __html: termsContent }}
                />
              </div>

              {hasAcknowledged && (
                <div className={styles.checkBoxOuter}>
                  <Checkbox checked={checked} onChange={() => setChecked(!checked)} />
                  <p className={styles.checkBoxLabel}>
                    I acknowledge and accept the Terms of Service
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className={styles.notFound}>{notFound}</p>
          )}

          <button
            type='button'
            className={clsx(
              loginStyles.submitBtn,
              isAcceptDisabled && loginStyles.disableBtn
            )}
            onClick={handlePrimaryAction}
            disabled={isAcceptDisabled}
          >
            {!submitting && (
              <span className='indicator-label'>
                {!hasAcknowledged ? 'Understood' : 'Accept & continue'}
              </span>
            )}
            {submitting && (
              <span className={styles.indicatorProgress}>
                Please wait...
                <span className={styles.spinner} />
              </span>
            )}
          </button>
        </main>
      </div>
    </AuthPageShell>
  );
}
